/**
 * LoaderJourneyCard.tsx — the small card under the journey entry loader that
 * shows THE JOURNEY, icon-led, one item after another, while the first read
 * runs — and GROWS as each item arrives.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: journey look-and-feel,
 * loaders (the entry loader's content card).
 *
 * THE ASKS (CTO, 2026-09-25). Evening, on the first cut's rotating panels:
 * "I don't want to have that carousel side of things, and then [the] ability
 * to pause. It should nicely load one after the other, showing the journey
 * till the main page loads." Then (~18:00), on the numbered "How it works"
 * list that replaced it: "Yes, you can make the card grow as the items appear,
 * but I think right now it's too much text. Let's try to use more icons … use
 * very catchy text … grab the attention because we have a very short window."
 * So: no carousel (still), and each item is an ICON in a soft tinted disc plus
 * a 2–5 word headline and at most one short line — scannable in a 3–4 second
 * glance — and the card is only as tall as what it has shown so far.
 *
 * WHAT IT IS. A card with a heading, the journey as a short list of STEPS
 * (`steps`: an icon, a headline, at most one line under it — a row of four on
 * a desktop, a column on a phone) and a few compact CHIPS under a hairline
 * (`chips`: a small icon, a bold headline and, where it adds meaning, a short
 * line run in after an em dash). Every item is in the DOM from the card's first
 * frame, in order; the items are REVEALED in order — the first with the card,
 * each next one {@link REVEAL_STEP_MS} later — and a waiting item takes NO
 * HEIGHT, so the card grows as each one opens (an animated `grid-template-rows`
 * 0fr → 1fr on the item, with a fade and a small rise). Once revealed an item
 * STAYS: nothing is replaced, removed or re-hidden, and the card simply builds
 * up until the read lands (at most {@link revealDoneMs} after its first frame,
 * then it is static). Rendered ONLY by `BrandedLoader`'s journey look, once its
 * mark is drawn (the 300ms show delay has passed), and it never holds the
 * loader up: it goes the moment the read lands, mid-reveal or not. The words
 * are the caller's (`journey/entryLoaderContent.ts`, and since 2026-09-25 the
 * checkout's `checkout/checkoutLoaderContent.ts` — the same card in front of
 * the plan screen, whose CALLER holds its loader for a set time on one lane;
 * this card still holds nothing); this file only shows them.
 *
 * WHY IT GROWS NOW (it did not in the numbered cut). That card was laid out at
 * its final height from its first frame so nothing below it moved — at the cost
 * of an empty white card waiting to be filled. The CTO asked for the card to
 * grow instead. What can move is the page BELOW the card (the site footer):
 * the card is the LAST block of the loader, so nothing the patient is reading
 * jumps — the ring, the status line and every revealed item stay exactly where
 * they are while the card extends downwards.
 *
 * ── ACCESSIBILITY ─────────────────────────────────────────────────────────
 *  - NOT A LIVE REGION, AND NOT INSIDE ONE. The loader's `role="status"` line
 *    says what is happening and stays the ONLY announcement. The card is a
 *    labelled section beside it.
 *  - THE FULL LIST IS IN THE ACCESSIBILITY TREE FROM THE CARD'S FIRST FRAME.
 *    The reveal is visual only: a waiting item is collapsed to zero height
 *    (its content clipped by `overflow: hidden`) and `opacity: 0` — never
 *    `display: none`, `visibility: hidden` or `aria-hidden`, and never added to
 *    the DOM later. So a screen-reader user can read the whole journey at their
 *    own pace the moment the card exists, nothing is announced item by item
 *    (items appearing in the DOM one by one would either be announced — chatter
 *    over the status line — or silently missed by someone who had already read
 *    past that point), and what they read never changes under them. The steps
 *    are a real ordered list; the icons are decoration (`aria-hidden`) — the
 *    words carry every meaning.
 *  - WCAG 2.2.2 (PAUSE, STOP, HIDE) NEEDS NO CONTROL HERE, and the card has
 *    none. The reveal is a one-time build-up that starts with the card,
 *    finishes within {@link revealDoneMs} of it (well inside five seconds:
 *    pinned by `entryLoaderContent.spec` for the real card) and is then STATIC
 *    — nothing updates again, nothing loops, nothing is replaced. Its movement
 *    is covered by 2.2.2's "less than five seconds" exception, and once static
 *    there is nothing left to pause, stop or hide. If a later change makes the
 *    card loop, replace an item or take longer than five seconds to settle, it
 *    needs a pause control again.
 *  - NOTHING FOCUSABLE. The card has no control, so focus can never be inside
 *    it when the loader unmounts. `LoaderJourneyCard.spec` pins it.
 *  - REDUCED MOTION: everything is shown at once, with no movement — every item
 *    revealed on the first frame (no timers at all), and the CSS draws no
 *    growth, fade or rise for the reduced cohort (`tokens.css` P11). The global
 *    reduced-motion kill is the second lock.
 *
 * ── WHAT MOVES (full-motion cohort) ──────────────────────────────────────
 * Each item, once, when it is revealed: its row track opens from 0fr to 1fr,
 * it fades in and it rises 8px. The rise is an animated `top` on the
 * relatively-positioned item — an OFFSET, never a `transform`: no element that
 * wraps words transforms, filters or sets `contain`, even mid-animation (the
 * loader CSS rule, `tokens.css` P11, pinned by `BrandedLoader.spec`). A step's
 * connector (the rail to the step before it) is part of the step, so it arrives
 * with the step it leads into and never dangles into empty space: on a phone it
 * is drawn on the step's ROW, inside the clip, so it opens with the step's
 * growth (review, 2026-09-25 — on the item it hung below the card for the first
 * part of each reveal); at ≥880px the horizontal rail is on the item, because it
 * must reach back past the clip into the column before (`tokens.css`).
 */
import { useEffect, useId, useState, type CSSProperties, type ReactElement } from 'react';
import { KeepCompoundsWhole } from './KeepCompoundsWhole';
import {
  IconChat,
  IconChecklist,
  IconParcel,
  IconPayments,
  IconPhone,
  IconPriceTag,
  IconStethoscope,
  IconStopwatch,
  type OutlineIconProps,
} from './icons';

/**
 * Which glyph leads an item — decoration; the words carry the meaning. `choose`
 * (a checklist) is the checkout loader's first step (2026-09-25,
 * `checkout/checkoutLoaderContent.ts`); the rest are shared by both cards.
 */
export type LoaderJourneyIcon = 'stopwatch' | 'choose' | 'card' | 'clinician' | 'parcel' | 'call' | 'save' | 'team';

/** One step of the journey. The words are the caller's, and must be public-safe (R28, Art 9). */
export interface LoaderJourneyStep {
  /** Stable id: the React key and the `data-item` hook. */
  id: string;
  icon: LoaderJourneyIcon;
  /** The headline: 2–5 words. */
  title: string;
  /** At most one short line under the headline. */
  detail?: string;
  /**
   * Words that CONTINUE the headline in the same sentence, at body weight —
   * "**Choose a plan and add a card** — it's only a hold" — instead of a second
   * line under it (2026-09-26, the CTO on "What happens next": "remove the
   * subtext under main point and connect the content there with the main
   * point"). Starts lower-case or with its own punctuation. Use it OR `detail`.
   */
  runIn?: string;
}

/** One chip under the journey. Public-safe copy only (R28, Art 9). */
export interface LoaderJourneyChip {
  /** Stable id: the React key and the `data-item` hook. */
  id: string;
  icon: LoaderJourneyIcon;
  /** The bold headline. */
  title: string;
  /**
   * An optional short line, run in after the headline and an em dash ("Switch
   * & Save — typically …"), so the chip reads as ONE sentence, to the eye and
   * to a screen reader. Starts lower-case.
   */
  detail?: string;
}

/**
 * What the card shows. Pass a STABLE object (a module constant), so a
 * re-render never hands the card a new list.
 */
export interface LoaderJourney {
  /** The card's visible heading — also its accessible name. */
  heading: string;
  /** The journey, revealed first, in this order. */
  steps: ReadonlyArray<LoaderJourneyStep>;
  /** The chips, revealed after the steps, in this order. */
  chips?: ReadonlyArray<LoaderJourneyChip>;
}

/**
 * THE STAGGER: each item is revealed this long after the one before it (the
 * first with the card). The brief: ~250–400ms an item, so seven short items
 * build up at a pace the eye can follow and the whole card is on screen within
 * ~3s of appearing.
 */
export const REVEAL_STEP_MS = 350;

/**
 * How long one item's growth, fade and rise take. The CSS reads this very
 * number (an inline custom property on the card), so the animation and the
 * schedule below can never disagree.
 */
export const REVEAL_FADE_MS = 450;

/** When item `index` (0-based, steps first, then chips) is revealed, from the card's first frame. */
export function revealAtMs(index: number): number {
  return index * REVEAL_STEP_MS;
}

/** When a card of `count` items has finished revealing (its last item fully grown in), from its first frame. */
export function revealDoneMs(count: number): number {
  return count > 0 ? revealAtMs(count - 1) + REVEAL_FADE_MS : 0;
}

/** The glyph for each icon key — the portal's own inline set (`ui/icons.tsx`; the CSP blocks external images). */
const ICONS: Record<LoaderJourneyIcon, (props: OutlineIconProps) => ReactElement> = {
  stopwatch: IconStopwatch,
  choose: IconChecklist,
  card: IconPayments,
  clinician: IconStethoscope,
  parcel: IconParcel,
  call: IconPhone,
  save: IconPriceTag,
  team: IconChat,
};

/** The drawn size of a step's icon and a chip's (px): the discs around them are CSS. */
const STEP_ICON_SIZE = 24;
const CHIP_ICON_SIZE = 18;

/**
 * The card's words: every hyphenated compound kept whole (`KeepCompoundsWhole`
 * — "weight-loss", "GPhC-registered"), and two shapes that component leaves
 * alone because a digit is not a letter: a figure range ("8–10%" — the first
 * v2 measurement broke it as "8–" / "10%") and a figure-led compound
 * ("2-minute"). The text is unchanged.
 */
function CardText({ text }: { text: string }) {
  const parts = text.split(/(\d+\s*[–-]\s*\d+%?|\d+-\p{L}+)/u);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className="gps-jux-nowrap">
            {part}
          </span>
        ) : (
          <KeepCompoundsWhole key={i} text={part} />
        ),
      )}
    </>
  );
}

/** A decorative icon in its tinted disc. */
function Badge({ icon, size, className }: { icon: LoaderJourneyIcon; size: number; className: string }) {
  const Icon = ICONS[icon];
  return (
    <span aria-hidden="true" className={className}>
      <Icon size={size} />
    </span>
  );
}

/** No chips: one shared empty list, so the default never changes identity. */
const NO_CHIPS: ReadonlyArray<LoaderJourneyChip> = [];

/**
 * `still`: the SAME card on a screen that is not waiting for anything (the
 * journey's "What happens next", 2026-09-26 — the CTO: "have it with the image
 * like we have in the loader"). Every item is on screen from the first frame and
 * nothing grows in: a build-up is for filling a wait, and on a screen the
 * patient reads at their own pace it would only delay the words. `headingHidden`
 * keeps the heading as the region's accessible name but off the screen, for a
 * page whose own title already says it.
 */
export function LoaderJourneyCard({
  journey,
  reduced,
  still = false,
  headingHidden = false,
}: {
  journey: LoaderJourney;
  reduced: boolean;
  still?: boolean;
  headingHidden?: boolean;
}) {
  const headingId = useId();
  const { heading, steps } = journey;
  const chips = journey.chips ?? NO_CHIPS;
  const count = steps.length + chips.length;
  // A still card is a reduced-motion card: everything at once, no timers.
  reduced = reduced || still;

  // How many items (in order: steps, then chips) have been revealed — the
  // first with the card. ONLY EVER UP.
  const [revealed, setRevealed] = useState(() => Math.min(1, count));
  // REDUCED MOTION: everything at once. The ONE rule for it, applied during
  // render (React's "storing information from previous renders" pattern), so
  // it holds on the card's first frame AND for a patient who turns reduced
  // motion on mid-reveal — and it sticks: turning it off again never hides a
  // thing. No frame is ever painted with fewer items than the one before it.
  if (reduced && revealed < count) setRevealed(count);

  // The schedule, armed ONCE from the card's first frame: every item's reveal
  // is its own timer at its own offset (not a chain re-armed after each
  // commit, which would drift by a render every step). Unmounting mid-reveal
  // clears them all — the loader goes the moment the read lands.
  useEffect(() => {
    if (reduced) return;
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    for (let index = 1; index < count; index += 1) {
      timers.push(setTimeout(() => setRevealed((r) => Math.max(r, index + 1)), revealAtMs(index)));
    }
    return () => timers.forEach(clearTimeout);
  }, [reduced, count]);

  if (count === 0) return null;

  const state = (index: number) => (index < revealed ? 'true' : 'false');

  return (
    <section
      aria-labelledby={headingId}
      className="gps-jux-entry-journey"
      data-revealed-count={revealed}
      data-still={still ? 'true' : undefined}
      // One number for the growth: the CSS animation reads it from here.
      style={{ '--gps-jux-reveal-ms': `${REVEAL_FADE_MS}ms` } as CSSProperties}
    >
      <p id={headingId} className={headingHidden ? 'gps-sr-only' : 'gps-jux-entry-journey-heading'}>
        {heading}
      </p>
      {steps.length > 0 ? (
        <ol className="gps-jux-entry-journey-steps">
          {steps.map((step, index) => (
            <li key={step.id} data-item={step.id} data-revealed={state(index)}>
              {/* The clip is what collapses a waiting item to zero height (its
                  row track is 0fr): the words stay in the DOM and in the
                  accessibility tree, they just take no room yet. */}
              <span className="gps-jux-entry-journey-clip">
                <span className="gps-jux-entry-journey-row">
                  <Badge icon={step.icon} size={STEP_ICON_SIZE} className="gps-jux-entry-journey-badge" />
                  <span className="gps-jux-entry-journey-body">
                    <span className="gps-jux-entry-journey-title">
                      <CardText text={step.title} />
                      {step.runIn ? (
                        <>
                          {' '}
                          <span className="gps-jux-entry-journey-runin">
                            <CardText text={step.runIn} />
                          </span>
                        </>
                      ) : null}
                    </span>
                    {step.detail ? (
                      <span className="gps-jux-entry-journey-detail">
                        <CardText text={step.detail} />
                      </span>
                    ) : null}
                  </span>
                </span>
              </span>
            </li>
          ))}
        </ol>
      ) : null}
      {chips.length > 0 ? (
        <ul className="gps-jux-entry-journey-chips">
          {chips.map((chip, n) => (
            <li
              key={chip.id}
              data-item={chip.id}
              data-revealed={state(steps.length + n)}
              // A chip with a run-in line gets the wider tile at ≥880px (tokens.css).
              data-detailed={chip.detail ? 'true' : 'false'}
            >
              <span className="gps-jux-entry-journey-clip">
                <span className="gps-jux-entry-journey-row">
                  <Badge icon={chip.icon} size={CHIP_ICON_SIZE} className="gps-jux-entry-journey-chip-badge" />
                  <span className="gps-jux-entry-journey-chip-text">
                    <strong className="gps-jux-entry-journey-chip-title">
                      <CardText text={chip.title} />
                    </strong>
                    {chip.detail ? (
                      <>
                        {' — '}
                        <span className="gps-jux-entry-journey-chip-detail">
                          <CardText text={chip.detail} />
                        </span>
                      </>
                    ) : null}
                  </span>
                </span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
